import { type AppLocale, SiteHeader, type SiteHeaderNavLink } from "@tessel/ui"
import { AppLink } from "@/components/AppLink"
import { useI18n } from "@/i18n"
import { useTheme } from "@/theme"

// Cross-domain link to the public community subdomain. Mirrors the constant in
// WebsiteVersionG so the source of truth stays one env-driven URL.
const COMMUNITY_URL =
  import.meta.env.VITE_COMMUNITY_URL || "https://community.tessel.app"

// Brand-specific gold gradient for the signup CTA. Kept inline because it is a
// marketing accent unique to the landing page, not a semantic primary that the
// other public apps share.
const SIGNUP_STYLE = {
  background: "linear-gradient(135deg, #d4b076, #e8d4a8)",
  color: "#0f0d1a",
}

/**
 * Landing-page header. Wraps the shared SiteHeader so tessel.app participates
 * in the same logo + nav + language picker + theme toggle pattern as
 * community/support/blog.
 *
 * The center nav exposes the two browseable destinations (Features, Community);
 * the right cluster pins the auth CTAs (Login + Signup) to the edge so the
 * conversion path is the last thing the eye lands on. The signup pill keeps
 * the custom gold gradient because it is the page's primary marketing accent
 * and reads well against both light and dark backgrounds.
 */
export const WebsiteHeader = () => {
  const { t, language, setLanguage } = useI18n()
  const { theme, toggleTheme } = useTheme()

  const links: SiteHeaderNavLink[] = [
    { label: t("nav.features"), href: "#features" },
    { label: t("nav.howItWorks"), href: "#how-it-works" },
    { label: t("nav.community"), href: COMMUNITY_URL, external: true },
  ]

  const onLocaleChange = (locale: AppLocale) => {
    setLanguage(locale as AppLocale)
  }

  const actions = (
    <>
      <AppLink
        href="/login"
        className="whitespace-nowrap text-base text-muted-foreground transition-colors hover:text-primary"
      >
        {t("nav.login")}
      </AppLink>
      <AppLink
        href="/signup"
        className="whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-all"
        style={SIGNUP_STYLE}
      >
        {t("nav.signup")}
      </AppLink>
    </>
  )

  return (
    <SiteHeader
      homeHref="/"
      logoAlt={t("logo.alt")}
      links={links}
      actions={actions}
      locale={language as AppLocale}
      onLocaleChange={onLocaleChange}
      theme={theme}
      onThemeToggle={toggleTheme}
      themeLabel={t("a11y.toggleTheme")}
      menuLabel={t("a11y.openMenu")}
    />
  )
}
